import type { GetTokenFn, TokenProvider, PowerSyncCredentials } from './types';

/**
 * Decode a JWT payload's `exp` claim into a Date, without verifying the
 * signature (PowerSync only needs the expiry hint; the server validates the
 * token). Returns undefined for anything that doesn't parse — callers then
 * simply omit `expiresAt`.
 */
function getTokenExpiry(token: string): Date | undefined {
  try {
    const parts = token.split('.');
    if (parts.length !== 3) return undefined;
    const base64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
    const padded = base64.padEnd(Math.ceil(base64.length / 4) * 4, '=');
    // atob + TextDecoder are available in browsers and modern React Native.
    const binary = atob(padded);
    const bytes = Uint8Array.from(binary, (c) => c.charCodeAt(0));
    const claims = JSON.parse(new TextDecoder().decode(bytes)) as { exp?: number };
    if (typeof claims.exp === 'number' && Number.isFinite(claims.exp)) {
      return new Date(claims.exp * 1000);
    }
  } catch {
    // Malformed/opaque token — skip the proactive-refresh hint.
  }
  return undefined;
}

/**
 * Creates a PowerSync-compatible token provider from a generic token getter.
 *
 * Returns `expiresAt` derived from the JWT's `exp` claim so PowerSync can
 * refresh the token (and re-authenticate the sync stream) *before* it lapses.
 * Without this, a long-lived session keeps the WebSocket alive on its original
 * token while later HTTP calls (e.g. getWriteCheckpoint / write-checkpoint2.json)
 * go out with a stale/expired token and get 401 — the checkpoint never
 * finalizes and the client shows "Last synced: Never".
 *
 * @param getToken - Function that retrieves the authentication token
 * @param endpoint - PowerSync endpoint URL (required)
 * @returns TokenProvider function for PowerSync client configuration
 *
 * @example
 * ```ts
 * const tokenProvider = makeTokenProvider(
 *   () => getIdToken(),
 *   'https://powersync.example.com'
 * );
 * ```
 */
export function makeTokenProvider(getToken: GetTokenFn, endpoint: string): TokenProvider {
  return async (): Promise<PowerSyncCredentials> => {
    const token = await getToken();
    const expiresAt = getTokenExpiry(token);
    return expiresAt ? { token, endpoint, expiresAt } : { token, endpoint };
  };
}
